import React, { useEffect, useMemo, useState } from "react";
import { Badge, Box, Card, Dialog, Flex, Grid, IconButton, Progress, SegmentedControl, Select, Table, Text,
  Tooltip } from "@radix-ui/themes";
import { CalendarIcon, ChatBubbleIcon, CheckCircledIcon, Cross2Icon, CursorArrowIcon, EnvelopeClosedIcon,
  ExclamationTriangleIcon, EyeOpenIcon, MobileIcon, PaperPlaneIcon, PersonIcon, ReloadIcon } from "@radix-ui/react-icons";
import { Area, CartesianGrid, ComposedChart, Line, ResponsiveContainer, Tooltip as RechartsTooltip,
  XAxis, YAxis } from "recharts";
import { useProfileContext } from "context";
import { useEmailActivityStats } from "hooks";
import MarketingWhatsappAnalytics from "./MarketingWhatsappAnalytics";

const RANGE_OPTIONS = [
  { label: "7 Days", value: 7 },
  { label: "30 Days", value: 30 },
  { label: "90 Days", value: 90 },
  { label: "12 Months", value: 365 },
];

const METRIC_ORDER = ["sent", "delivered", "opened", "clicked", "bounced", "unsubscribed"];

const METRIC_ICONS = {
  sent: PaperPlaneIcon,
  delivered: CheckCircledIcon,
  opened: EyeOpenIcon,
  clicked: CursorArrowIcon,
  bounced: ExclamationTriangleIcon,
  unsubscribed: PersonIcon,
};

const METRIC_TONES = {
  sent: "violet",
  delivered: "violet",
  opened: "violet",
  clicked: "violet",
  bounced: "gray",
  unsubscribed: "gray",
};

const METRIC_LABELS = {
  sent: "Total Sent",
  delivered: "Delivered",
  opened: "Opened",
  clicked: "Clicked",
  bounced: "Bounced",
  unsubscribed: "Unsubscribed",
};

const CAMPAIGN_COMPARISON_COLUMNS = [
  { key: "campaign", label: "Campaign", width: 300, align: "left" },
  { key: "sent", label: "Sent", width: 130, align: "right" },
  { key: "delivered", label: "Delivered", width: 150, align: "right" },
  { key: "opened", label: "Opened", width: 140, align: "right" },
  { key: "clicked", label: "Clicked", width: 140, align: "right" },
  { key: "bounced", label: "Bounced", width: 140, align: "right" },
  { key: "unsubscribed", label: "Unsub", width: 120, align: "right" },
];

const ANALYTICS_TABS = [
  { value: "overview", label: "Overview" },
  { value: "funnel", label: "Funnel" },
];

const FUNNEL_DROPOFF_LABELS = {
  delivered: "didn't receive it",
  opened: "didn't open it",
  clicked: "didn't click",
  visited: "didn't visit",
};

const defaultMetric = (key) => ({
  key,
  label: METRIC_LABELS[key] || key,
  value: 0,
  previous_value: 0,
  change_percent: 0,
  lower_is_good: false,
  series: { daily: [], weekly: [], monthly: [] },
  summary: {
    period_high: { value: 0, date: null },
    period_low: { value: 0, date: null },
    daily_average: 0,
  },
});

const formatNumber = (value) => Number(value || 0).toLocaleString();

const formatDate = (value) => {
  if (!value) return "";
  return new Date(`${value}T00:00:00`).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
  });
};

const formatChange = (value) => `${Math.abs(Number(value || 0)).toFixed(1)}%`;

const formatPercent = (value) => `${Number(value || 0).toFixed(1).replace(/\.0$/, "")}%`;

const isPositiveTrend = (metric) => {
  const change = Number(metric.change_percent || 0);
  if (change === 0) return true;
  return metric.lower_is_good ? change < 0 : change > 0;
};

function MiniSparkline({ metric }) {
  const points = metric?.series?.daily || [];
  const chartData = points.map((point) => ({
    date: formatDate(point.date),
    value: Number(point.count || 0),
  }));
  const values = chartData.map((point) => point.value);
  const hasData = values.some((value) => value > 0);

  if (!hasData) {
    return (
      <Flex align="center" style={{ height: "100%" }}>
        <Text size="1" color="gray">No activity in this period</Text>
      </Flex>
    );
  }

  return (
    <ResponsiveContainer width="100%" height="100%">
      <ComposedChart data={chartData} margin={{ top: 4, right: 2, left: 2, bottom: 4 }}>
        <Line
          type="linear"
          dataKey="value"
          stroke="var(--violet-9)"
          strokeWidth={1.5}
          dot={false}
          activeDot={false}
          isAnimationActive={false}
        />
      </ComposedChart>
    </ResponsiveContainer>
  );
}

function MetricChartTooltip({ active, payload, label, metric }) {
  if (!active || !payload?.length) return null;

  return (
    <Card
      style={{
        padding: "var(--space-3)",
        border: "1px solid var(--gray-a6)",
        borderRadius: "var(--radius-2)",
        backgroundColor: "var(--color-background)",
        boxShadow: "0px 12px 32px -16px var(--gray-a5)",
      }}
    >
      <Flex direction="column" gap="2">
        <Text size="2" weight="medium">{label}</Text>
        <Flex align="center" justify="between" gap="5">
          <Text size="2" color="gray">{metric?.label}</Text>
          <Text size="2" weight="medium">{formatNumber(payload[0]?.value)}</Text>
        </Flex>
      </Flex>
    </Card>
  );
}

const getMetricXAxisInterval = (count) => {
  if (count > 8) return Math.ceil(count / 8) - 1;
  return 0;
};

const getMetricYAxisMax = (dataMax) => {
  const max = Number(dataMax || 0);
  if (max <= 0) return 1;
  return Math.ceil(max * 1.15);
};

function MetricXAxisTick({ x, y, payload }) {
  return (
    <g transform={`translate(${x},${y})`}>
      <text
        x={0}
        y={0}
        dy={16}
        textAnchor="middle"
        fill="var(--gray-11)"
        fontSize="12px"
      >
        {payload.value}
      </text>
    </g>
  );
}

function MetricYAxisTick({ x, y, payload }) {
  return (
    <g transform={`translate(${x},${y})`}>
      <text
        x={8}
        y={4}
        textAnchor="start"
        fill="var(--gray-11)"
        fontSize="12px"
      >
        {formatNumber(payload.value)}
      </text>
    </g>
  );
}

function MetricLineChart({ metric, granularity = "daily" }) {
  const points = metric?.series?.[granularity] || [];
  const chartData = points.map((point) => ({
    date: formatDate(point.date),
    value: Number(point.count || 0),
  }));
  const values = chartData.map((point) => point.value);
  const showEmpty = values.length === 0 || values.every((value) => value === 0);

  if (showEmpty) {
    return (
      <Flex align="center" justify="center" style={{ height: "100%" }}>
        <Text size="2" color="gray">No activity for this period</Text>
      </Flex>
    );
  }

  return (
    <ResponsiveContainer width="100%" height="100%">
      <ComposedChart data={chartData} margin={{ top: 16, right: 12, left: 28, bottom: 4 }}>
        <defs>
          <linearGradient id="emailMetricArea" x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%" stopColor="var(--violet-8)" stopOpacity={0.35} />
            <stop offset="95%" stopColor="var(--violet-8)" stopOpacity={0.08} />
          </linearGradient>
        </defs>
        <CartesianGrid
          stroke="var(--gray-6)"
          strokeWidth={1}
          strokeDasharray="2 2"
          vertical
          horizontal
        />
        <XAxis
          dataKey="date"
          tickLine={false}
          axisLine={{ stroke: "var(--gray-6)", strokeWidth: 1, strokeDasharray: "2 2" }}
          tick={<MetricXAxisTick />}
          tickSize={0}
          interval={getMetricXAxisInterval(chartData.length)}
          minTickGap={28}
        />
        <YAxis
          tickLine={false}
          axisLine={false}
          tick={<MetricYAxisTick />}
          tickFormatter={formatNumber}
          allowDecimals={false}
          domain={[0, getMetricYAxisMax]}
          width={48}
          orientation="right"
        />
        <RechartsTooltip content={<MetricChartTooltip metric={metric} />} cursor={false} />
        <Area
          type="linear"
          dataKey="value"
          stroke="none"
          fill="url(#emailMetricArea)"
          fillOpacity={1}
          isAnimationActive={false}
        />
        <Line
          type="linear"
          dataKey="value"
          stroke="var(--violet-9)"
          strokeWidth={1.5}
          dot={{
            r: 3,
            strokeWidth: 1.5,
            fill: "var(--color-panel-solid)",
            stroke: "var(--violet-9)",
          }}
          activeDot={{ r: 5, strokeWidth: 1.5, fill: "var(--violet-9)", stroke: "var(--color-panel-solid)" }}
        />
      </ComposedChart>
    </ResponsiveContainer>
  );
}

function TrendBadge({ metric }) {
  const change = Number(metric.change_percent || 0);
  const positive = isPositiveTrend(metric);
  const arrow = change < 0 ? "↓" : "↑";

  return (
    <Badge color={positive ? "green" : "red"} variant="soft" size="1">
      {arrow} {formatChange(change)}
    </Badge>
  );
}

function AnalyticsCard({ metric, onOpen }) {
  const Icon = METRIC_ICONS[metric.key] || PaperPlaneIcon;
  const tone = METRIC_TONES[metric.key] || "violet";

  return (
    <Card
      style={{
        height: "100%",
        minHeight: 172,
        padding: 0,
        cursor: "pointer",
      }}
    >
      <button
        type="button"
        onClick={() => onOpen(metric)}
        style={{
          appearance: "none",
          background: "transparent",
          border: 0,
          color: "inherit",
          display: "block",
          font: "inherit",
          height: "100%",
          padding: 0,
          textAlign: "left",
          width: "100%",
        }}
      >
        <Flex direction="column" gap="3" height="100%" p="4">
          <Flex justify="between" align="start" gap="3">
            <Flex align="center" gap="2" style={{ minWidth: 0 }}>
              <Icon
                width={16}
                height={16}
                color={tone === "violet" ? "var(--violet-9)" : "var(--gray-11)"}
              />
              <Text size="2" weight="medium" color="gray" style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                {metric.label}
              </Text>
            </Flex>
            <TrendBadge metric={metric} />
          </Flex>

          <Flex direction="column" gap="2">
            <Text as="div" size="7" weight="bold" style={{ letterSpacing: 0, lineHeight: 1 }}>
              {formatNumber(metric.value)}
            </Text>
            {metric.lower_is_good && (
              <Badge color="green" variant="soft" size="1" style={{ width: "fit-content" }}>
                Within healthy range
              </Badge>
            )}
          </Flex>

          <Box style={{ height: 42, marginTop: "auto" }}>
            <MiniSparkline metric={metric} />
          </Box>
        </Flex>
      </button>
    </Card>
  );
}

function CampaignComparisonTable({ campaigns = [] }) {
  return (
    <>
    <Flex direction="column" style={{ flex: "0 0 auto" }}>
      <Text size="4" weight="bold">Campaign Comparison</Text>
      <Text size="2" color="gray">Includes only campaigns actually sent during the selected period.</Text>
    </Flex>
    <Card style={{ display: "flex", alignSelf: "stretch", flexDirection: "column", alignItems: "flex-start",
      flex: "0 0 auto", maxHeight: 320, maxWidth: "100%", overflow: "auto" }}>
      <Table.Root layout="fixed" style={{ width: "100%", height: "100%", minWidth: 0, flex: "1 0 0" }}>
        <Table.Header style={{ position: "sticky", top: 0, zIndex: 1, backgroundColor: "var(--color-panel-solid)" }}>
          <Table.Row align="center">
            {CAMPAIGN_COMPARISON_COLUMNS.map((column) => (
              <Table.ColumnHeaderCell
                key={column.key}
                style={{
                  width: column.width,
                  textAlign: column.align,
                  whiteSpace: "nowrap",
                }}
              >
                {column.label}
              </Table.ColumnHeaderCell>
            ))}
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {campaigns.length > 0 ? (
            campaigns.map((campaign) => (
              <Table.Row key={campaign.campaign_id || campaign.campaign}>
                {CAMPAIGN_COMPARISON_COLUMNS.map((column) => (
                  <Table.Cell
                    key={column.key}
                    style={{
                      textAlign: column.align,
                      whiteSpace: "nowrap",
                      overflow: column.key === "campaign" ? "hidden" : undefined,
                      textOverflow: column.key === "campaign" ? "ellipsis" : undefined,
                    }}
                  >
                    {column.key === "campaign"
                      ? (
                        <Text size="2" weight="medium">
                          {campaign.campaign || "Untitled campaign"}
                        </Text>
                      )
                      : (
                        <Text size="2" color="gray">
                          {formatNumber(campaign[column.key])}
                        </Text>
                      )}
                  </Table.Cell>
                ))}
              </Table.Row>
            ))
          ) : (
            <Table.Row>
              <Table.Cell colSpan={CAMPAIGN_COMPARISON_COLUMNS.length}>
                <Flex align="center" justify="center" py="6">
                  <Text size="2" color="gray">No campaigns to compare</Text>
                </Flex>
              </Table.Cell>
            </Table.Row>
          )}
        </Table.Body>
      </Table.Root>
    </Card>
    </>
  );
}

function AnalyticsTabs({ value, onValueChange }) {
  return (
    <Flex
      align="center"
      gap="5"
      style={{
        borderBottom: "1px solid var(--gray-a5)",
        width: "100%",
        flex: "0 0 auto",
      }}
    >
      {ANALYTICS_TABS.map((tab) => {
        const active = value === tab.value;
        return (
          <button
            key={tab.value}
            type="button"
            onClick={() => onValueChange(tab.value)}
            style={{
              appearance: "none",
              background: "transparent",
              border: 0,
              borderBottom: active ? "2px solid var(--violet-9)" : "2px solid transparent",
              color: active ? "var(--violet-11)" : "var(--gray-11)",
              cursor: "pointer",
              font: "inherit",
              fontSize: 14,
              fontWeight: active ? 600 : 500,
              padding: "0 0 var(--space-3)",
            }}
          >
            {tab.label}
          </button>
        );
      })}
    </Flex>
  );
}

function FunnelRow({ step, index }) {
  const isAvailable = step.available !== false;
  const width = Math.min(
    100,
    Math.max(Number(step.rate || 0), isAvailable && Number(step.value || 0) > 0 ? 3 : 0),
  );
  const isVisited = step.key === "visited";

  return (
    <Box
      style={{
        display: "grid",
        gridTemplateColumns: "minmax(120px, 160px) minmax(0, 1fr) 64px",
        columnGap: "var(--space-4)",
        alignItems: "center",
      }}
    >
      <Box>
        <Text as="div" size="2" weight="bold">{step.label}</Text>
        <Text as="div" size="1" color="gray" style={{ lineHeight: 1.15 }}>
          {step.description}
        </Text>
      </Box>

      <Box
        style={{
          height: 26,
          borderRadius: "var(--radius-2)",
          background: "var(--gray-a3)",
          overflow: "hidden",
        }}
      >
        <Flex
          align="center"
          style={{
            width: `${width}%`,
            minWidth: Number(step.value || 0) > 0 ? 48 : 0,
            height: "100%",
            borderRadius: "var(--radius-2)",
            background: isVisited ? "var(--green-9)" : `var(--violet-${Math.min(9, 9 + index)})`,
            color: "white",
            paddingInline: Number(step.value || 0) > 0 ? "var(--space-2)" : 0,
            transition: "width 180ms ease",
          }}
        >
          {isAvailable && Number(step.value || 0) > 0 && (
            <Text size="1" weight="bold" style={{ color: "inherit" }}>
              {formatNumber(step.value)}
            </Text>
          )}
        </Flex>
      </Box>

      <Text size="2" weight="bold" align="right">
        {isAvailable ? formatPercent(step.rate) : "—"}
      </Text>
    </Box>
  );
}

function FunnelDropoff({ step }) {
  const dropoffLabel = FUNNEL_DROPOFF_LABELS[step.key];
  if (!dropoffLabel || step.available === false) return null;

  return (
    <Box
      style={{
        display: "grid",
        gridTemplateColumns: "minmax(120px, 160px) minmax(0, 1fr) 64px",
        columnGap: "var(--space-4)",
        alignItems: "center",
        marginTop: "-6px",
      }}
    >
      <Box />
      <Text size="1" color="red">
        ↓ ↓ {formatPercent(step.dropoff_rate)} {dropoffLabel}
      </Text>
      <Box />
    </Box>
  );
}

function ConversionFunnel({ funnel, selectedCampaignId, onCampaignChange }) {
  const campaignOptions = funnel?.campaigns || [];
  const activeFunnel = funnel?.aggregate;
  const steps = activeFunnel?.steps || [];

  return (
    <Flex direction="column" gap="4" width="100%">
      <Flex justify="between" align="end" gap="3" wrap="wrap">
        <Flex direction="column" style={{ flex: "0 0 auto" }}>
          <Text size="4" weight="bold">Conversion Funnel</Text>
          <Text size="2" color="gray">
            Unique deliveries with {activeFunnel?.attribution_window_days || 30}-day last-click venue attribution
          </Text>
        </Flex>

        <Flex align="center" gap="2">
          <Text size="2" color="gray">Viewing funnel for:</Text>
          <Select.Root value={selectedCampaignId} onValueChange={onCampaignChange}>
            <Select.Trigger style={{ minWidth: 260 }} />
            <Select.Content>
              <Select.Item value="all">All Campaigns (Aggregate)</Select.Item>
              {campaignOptions.map((campaign) => (
                <Select.Item key={campaign.campaign_id} value={campaign.campaign_id}>
                  {campaign.campaign || "Untitled campaign"}
                </Select.Item>
              ))}
            </Select.Content>
          </Select.Root>
        </Flex>
      </Flex>

      {funnel?.attribution_available === false && (
        <Card style={{ background: "var(--amber-a2)", borderColor: "var(--amber-a6)" }}>
          <Flex align="center" gap="2" p="3">
            <ExclamationTriangleIcon color="var(--amber-9)" />
            <Text size="2" color="gray">
              {selectedCampaignId === "all"
                ? "Venue attribution was too large to calculate safely. Select a campaign to run a smaller query."
                : "Venue attribution was too large to calculate safely. Choose a shorter date range and retry."}
            </Text>
          </Flex>
        </Card>
      )}

      <Card style={{ padding: 0, overflow: "hidden" }}>
        <Flex direction="column" gap="3" p="3">
          {steps.length > 0 ? (
            steps.map((step, index) => (
              <React.Fragment key={step.key}>
                {index > 0 && <FunnelDropoff step={step} />}
                <FunnelRow step={step} index={index} />
              </React.Fragment>
            ))
          ) : (
            <Flex align="center" justify="center" py="6">
              <Text size="2" color="gray">No funnel data available</Text>
            </Flex>
          )}
        </Flex>
      </Card>
    </Flex>
  );
}

function SummaryCard({ label, value, detail, color }) {
  return (
    <Card style={{ background: `var(--${color}-a2)`, borderColor: `var(--${color}-a6)` }}>
      <Flex direction="column" gap="2" p="3">
        <Text size="2" color="gray" weight="medium">{label}</Text>
        <Text size="6" weight="bold" color={color}>{formatNumber(value)}</Text>
        <Text size="2" color="gray">{detail}</Text>
      </Flex>
    </Card>
  );
}

function AnalyticsLoadError({ message = "Analytics could not be loaded. Use refresh to try again." }) {
  return (
    <Card style={{ width: "100%" }}>
      <Flex align="center" justify="center" gap="2" py="6">
        <ExclamationTriangleIcon color="var(--red-9)" />
        <Text size="2" color="gray">{message}</Text>
      </Flex>
    </Card>
  );
}

function MetricDialog({ metric, open, onOpenChange, error }) {
  const [granularity, setGranularity] = useState("daily");
  const summary = metric?.summary || defaultMetric("empty").summary;
  const changeIsGood = metric ? isPositiveTrend(metric) : true;

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Content
        style={{
          width: "min(820px, calc(100vw - 48px))",
          maxWidth: 820,
          maxHeight: "min(720px, calc(100vh - 80px))",
          padding: 0,
          overflow: "hidden",
        }}
      >
        <Flex justify="between" align="start" p="4">
          <Box>
            <Dialog.Title mb="1">{metric?.label}</Dialog.Title>
            <Text as="div" size="7" weight="bold" color="violet">
              {formatNumber(metric?.value)}
            </Text>
            <Badge color={changeIsGood ? "green" : "red"} variant="soft" size="1" mt="2">
              <Flex align="center" gap="1">
                {Number(metric?.change_percent || 0) < 0 ? "↓" : "↑"}
                {formatChange(metric?.change_percent)} vs previous period
              </Flex>
            </Badge>
          </Box>

          <Dialog.Close>
            <IconButton variant="ghost" color="gray" aria-label="Close chart">
              <Cross2Icon width={18} height={18} />
            </IconButton>
          </Dialog.Close>
        </Flex>

        <Box style={{ borderTop: "1px solid var(--gray-a5)" }} p="4">
          <SegmentedControl.Root value={granularity} onValueChange={setGranularity}>
            <SegmentedControl.Item value="daily">Daily</SegmentedControl.Item>
            <SegmentedControl.Item value="weekly">Weekly</SegmentedControl.Item>
            <SegmentedControl.Item value="monthly">Monthly</SegmentedControl.Item>
          </SegmentedControl.Root>

          <Box style={{ height: 280, marginTop: "var(--space-4)" }}>
            {error ? (
              <Flex align="center" justify="center" height="100%">
                <Text size="2" color="gray">The detailed chart could not be loaded.</Text>
              </Flex>
            ) : metric && (
              <MetricLineChart metric={metric} granularity={granularity} />
            )}
          </Box>

          <Grid columns={{ initial: "1", sm: "3" }} gap="3" mt="4">
            <SummaryCard
              label="Period High"
              value={summary.period_high?.value}
              detail={formatDate(summary.period_high?.date)}
              color="green"
            />
            <SummaryCard
              label="Period Low"
              value={summary.period_low?.value}
              detail={formatDate(summary.period_low?.date)}
              color="red"
            />
            <SummaryCard
              label="Daily Average"
              value={summary.daily_average}
              detail="per day"
              color="violet"
            />
          </Grid>
        </Box>
      </Dialog.Content>
    </Dialog.Root>
  );
}

export default function MarketingAnalytics() {
  const { userProfile } = useProfileContext();
  const [selectedSegment, setSelectedSegment] = useState("email");
  const [rangeDays, setRangeDays] = useState(30);
  const [activeMetric, setActiveMetric] = useState(null);
  const [analyticsTab, setAnalyticsTab] = useState("overview");
  const [selectedFunnelCampaignId, setSelectedFunnelCampaignId] = useState("all");

  useEffect(() => {
    setSelectedFunnelCampaignId("all");
  }, [rangeDays, userProfile?.organisation_id]);
  const {
    stats: overviewStats,
    isLoading: isOverviewLoading,
    isFetching: isOverviewFetching,
    error: overviewError,
    refetch: refetchOverview,
  } = useEmailActivityStats(
    userProfile?.organisation_id,
    { channel: "email" },
    rangeDays,
    {
      section: "overview",
      enabled: selectedSegment === "email",
    },
  );
  const {
    stats: funnelStats,
    isLoading: isFunnelLoading,
    isFetching: isFunnelFetching,
    error: funnelError,
    refetch: refetchFunnel,
  } = useEmailActivityStats(
    userProfile?.organisation_id,
    { channel: "email" },
    rangeDays,
    {
      section: "funnel",
      campaignId: selectedFunnelCampaignId,
      keepPreviousData: true,
      enabled: selectedSegment === "email" && analyticsTab === "funnel",
    },
  );
  const activeMetricKey = activeMetric?.key || "";
  const {
    stats: metricDetailStats,
    isLoading: isMetricLoading,
    isFetching: isMetricFetching,
    error: metricError,
    refetch: refetchMetric,
  } = useEmailActivityStats(
    userProfile?.organisation_id,
    { channel: "email" },
    rangeDays,
    {
      section: "metric",
      metric: activeMetricKey,
      enabled: selectedSegment === "email" && !!activeMetricKey,
    },
  );
  const campaignComparison = overviewStats?.campaign_comparison || [];
  const funnel = funnelStats?.funnel || { aggregate: null, campaigns: [] };
  const loadedFunnelCampaignId = funnel.selected_campaign_id
    || funnel.aggregate?.campaign_id
    || "all";
  const isFunnelSelectionLoading = isFunnelFetching
    && loadedFunnelCampaignId !== selectedFunnelCampaignId;

  const metrics = useMemo(() => {
    const metricMap = overviewStats?.metrics || {};
    return METRIC_ORDER.map((key) => ({
      ...defaultMetric(key),
      ...(metricMap[key] || {}),
    }));
  }, [overviewStats?.metrics]);

  const dialogMetric = useMemo(() => {
    if (!activeMetric) return null;
    const detail = metricDetailStats?.metrics?.[activeMetric.key];
    if (!detail) return activeMetric;
    return {
      ...activeMetric,
      ...detail,
      series: {
        ...(activeMetric.series || {}),
        ...(detail.series || {}),
      },
    };
  }, [activeMetric, metricDetailStats?.metrics]);

  const isLoading = isOverviewLoading
    || (analyticsTab === "funnel" && isFunnelLoading)
    || (!!activeMetricKey && isMetricLoading);
  const isRefreshing = isOverviewFetching
    || (analyticsTab === "funnel" && isFunnelFetching)
    || (!!activeMetricKey && isMetricFetching);

  const handleRefresh = () => {
    const requests = [refetchOverview()];
    if (analyticsTab === "funnel") requests.push(refetchFunnel());
    if (activeMetricKey) requests.push(refetchMetric());
    return Promise.all(requests);
  };

  const renderAnalyticsContent = () => {
    if (selectedSegment === "whatsapp") {
      return (
        <MarketingWhatsappAnalytics />
      );
    }
    else {
      if (overviewError) {
        return <AnalyticsLoadError />;
      }
      return (
        <>
          <Flex direction="column" gap="4" width="100%" style={{ minHeight: 0, flex: "0 0 auto" }}>
            <Grid columns={{ initial: "1", sm: "2", lg: "3" }} gap="4" width="100%" style={{ flex: "0 0 auto" }}>
              {metrics.map((metric) => (
                <AnalyticsCard key={metric.key} metric={metric} onOpen={setActiveMetric} />
              ))}
            </Grid>
            <AnalyticsTabs value={analyticsTab} onValueChange={setAnalyticsTab} />
            {analyticsTab === "overview" ? (
              <CampaignComparisonTable campaigns={campaignComparison} />
            ) : funnelError ? (
              <AnalyticsLoadError message="The conversion funnel could not be loaded. Use refresh to try again." />
            ) : isFunnelLoading || isFunnelSelectionLoading ? (
              <Card style={{ width: "100%" }}>
                <Flex align="center" justify="center" py="6">
                  <Text size="2" color="gray">
                    {selectedFunnelCampaignId === "all"
                      ? "Loading conversion funnel…"
                      : "Loading selected campaign funnel…"}
                  </Text>
                </Flex>
              </Card>
            ) : (
              <ConversionFunnel
                funnel={funnel}
                selectedCampaignId={selectedFunnelCampaignId}
                onCampaignChange={setSelectedFunnelCampaignId}
              />
            )}
          </Flex>
          <MetricDialog
            metric={dialogMetric}
            open={!!activeMetric}
            onOpenChange={(open) => !open && setActiveMetric(null)}
            error={metricError}
          />
        </>
      );
    }
  };

  return (
    <>
      {/* Loading Progress Bar */}
      {isLoading && (
        <div style={{ position: "fixed", top: 0, left: 0, width: "100%", zIndex: 1000 }}>
          <Progress size="1" duration="1" />
        </div>
      )}

      <Flex direction="column" gap="5" style={{ alignSelf: "stretch", alignItems: "flex-start", flex: "1 0 0", 
        minHeight: 0, height: "100%", width: "100%" }}>
        {/* Filter Segmented Control */}
        <Flex justify="between" align="center" style={{ alignSelf: "stretch", flexShrink: 0 }}>
          <SegmentedControl.Root value={selectedSegment} onValueChange={setSelectedSegment}>
            <SegmentedControl.Item value="email">
              <Flex gap="1" align="center">
                <EnvelopeClosedIcon />
                <Text>Email</Text>
              </Flex>
            </SegmentedControl.Item>
            <SegmentedControl.Item value="sms">
              <Flex gap="1" align="center">
                <MobileIcon />
                <Text>SMS</Text>
              </Flex>
            </SegmentedControl.Item>
            <SegmentedControl.Item value="whatsapp">
              <Flex gap="1" align="center">
                <ChatBubbleIcon />
                <Text>WhatsApp</Text>
              </Flex>
            </SegmentedControl.Item>
          </SegmentedControl.Root>

          {selectedSegment === "email" && (
            <Flex align="center" gap="2">
              <CalendarIcon />
              <Text size="2" color="gray">Period</Text>
              <Select.Root
                value={String(rangeDays)}
                onValueChange={(value) => setRangeDays(Number(value))}
              >
                <Select.Trigger aria-label="Analytics period" style={{ minWidth: 120 }} />
                <Select.Content>
                  {RANGE_OPTIONS.map((option) => (
                    <Select.Item key={option.value} value={String(option.value)}>
                      {option.label}
                    </Select.Item>
                  ))}
                </Select.Content>
              </Select.Root>
              <Tooltip content="Refresh analytics data" side="top">
                <IconButton
                  type="button"
                  variant="soft"
                  color="gray"
                  disabled={isRefreshing}
                  onClick={handleRefresh}
                >
                  <ReloadIcon />
                </IconButton>
              </Tooltip>
            </Flex>
          )}
        </Flex>

        {selectedSegment === "sms" ? (
          <Flex direction="column" style={{ justifyContent: 'center', alignItems: 'center', alignSelf: 'stretch',
            width: "100%", border: '1px dashed var(--gray-a6)', borderRadius: 'var(--radius-4)', height: "100%",
            flex: '1 0 0', padding: 'var(--space-6)' }}>
            <Text color="gray">SMS analytics coming soon.</Text>
          </Flex>
        ) : (
          <Flex direction="column" gap="5" style={{ alignSelf: "stretch", width: "100%", overflow: "auto",
            height: "100%", flex: "1 0 0", minHeight: 0, paddingRight: 4 }}>
            {renderAnalyticsContent()}
          </Flex>
        )}
    </Flex>
    </>
  );
}
